import Button from '@components/button/Button';
import {
  Dropdown as AntDesignDropdown,
  Divider,
  DropdownProps,
  MenuProps,
  Typography,
  theme,
} from 'antd';
import cx from 'classnames';
import { isEmpty, isNil } from 'ramda';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { MdCheck } from 'react-icons/md';

import styles from './_Dropdown.module.scss';

interface IOption {
  key: string | number;
  label: string | number | ReactElement;
  disabled?: boolean;
  icon?: ReactElement;
}

type SelectProps = {
  options: IOption[];
  defaultValue?: string;
  title?: string;
  value?: string | string[];
  iconRight?: boolean;
  onChange?: (value: string | string[]) => void;
  children: ReactElement;
  onDropdownChange?: (value: boolean) => void;
  enablePopupParentContainer?: boolean;
  multipleMode?: boolean;
  dropdownClassname?: string;
  labelOkButton?: string;
  labelResetButton?: string;
  onResetButtonClick?: any;
  arrow?: boolean;
  placement?:
    | 'bottom'
    | 'bottomLeft'
    | 'bottomRight'
    | 'top'
    | 'topLeft'
    | 'topRight';
};

const { Text } = Typography;

const { useToken } = theme;

const Dropdown: React.FC<SelectProps> = ({
  title,
  multipleMode,
  options,
  defaultValue,
  iconRight,
  onChange,
  children,
  value,
  onDropdownChange,
  enablePopupParentContainer = true,
  dropdownClassname = '',
  labelOkButton,
  labelResetButton,
  onResetButtonClick,
  placement = 'bottomRight',
  arrow = false,
}) => {
  const { token } = useToken();

  const [isOpen, setIsOpen] = useState(false);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const clickedKey = e.key;

    if (multipleMode) {
      const exists = (value || []).includes(clickedKey);
      const newValue = exists
        ? ((value as string[]) || []).filter(
            (key: string) => key !== clickedKey,
          )
        : [...(value || []), clickedKey];
      onChange?.(newValue);
    } else {
      onChange?.(clickedKey);
      onDropdownChange?.(false);
    }
  };

  const checkIfOptionSelected = (
    key: string | number | undefined,
    val: (string | number)[] | undefined | string | number,
  ) => {
    if (isNil(key)) {
      return false;
    }
    return Array.isArray(val) ? val?.includes(key) : key === val;
  };

  const renderMenuOption = useCallback(
    (option: IOption) => ({
      ...option,
      icon: undefined,
      label: (
        <div className="tw-w-full tw-h-full tw-flex tw-justify-between tw-items-center">
          <div className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-overflow-x-hidden">
            <div className="tw-flex tw-items-center tw-shrink-0">
              {!iconRight && Boolean(option?.icon) && option?.icon}
            </div>
            <Text className="tw-text-inherit" ellipsis>
              {option?.label}
            </Text>
            <div className="tw-flex tw-items-center tw-shrink-0">
              {iconRight && Boolean(option?.icon) && option?.icon}
            </div>
          </div>
          {checkIfOptionSelected(option?.key, value) && (
            <MdCheck className="tw-shrink-0" size={16} />
          )}
        </div>
      ),
    }),
    [iconRight, value],
  );

  const menuOptions = useMemo(
    () => options?.map(renderMenuOption),
    [options, renderMenuOption],
  );

  if (isNil(options) || isEmpty(options)) {
    return null;
  }

  const onDropdownOpenChange: DropdownProps['onOpenChange'] = (
    nextOpen,
    info,
  ) => {
    if (info.source === 'trigger' || nextOpen) {
      if (multipleMode) {
        setIsOpen(nextOpen);
      }
      onDropdownChange?.(nextOpen);
    }
  };

  const getPopupContainer = (trigger: HTMLElement) =>
    enablePopupParentContainer
      ? (trigger.parentElement as HTMLElement)
      : document.body;

  const contentStyle: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
  };

  const menuStyle: React.CSSProperties = {
    boxShadow: 'none',
  };

  const renderPopup = (menu: React.ReactNode) => (
    <div style={contentStyle} className={cx({ 'tw-pt-2': Boolean(title) })}>
      {title && (
        <Text strong className="tw-px-4">
          {title}
        </Text>
      )}

      {React.cloneElement(
        menu as React.ReactElement<{
          style: React.CSSProperties;
        }>,
        { style: menuStyle },
      )}
      <Divider style={{ margin: 0 }} />
      {multipleMode && (
        <div className="tw-flex tw-justify-between tw-px-2 tw-py-2">
          <Button
            type="link"
            label={labelResetButton}
            onClick={() =>
              onResetButtonClick ? onResetButtonClick() : onChange?.([])
            }
          />
          <Button
            type="primary"
            label={labelOkButton}
            onClick={() => {
              setIsOpen(false);
              onDropdownChange?.(false);
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <AntDesignDropdown
      menu={{
        items: menuOptions,
        defaultSelectedKeys: !isNil(defaultValue) ? [defaultValue] : undefined,
        selectedKeys: multipleMode ? (value as string[]) : [value as string],
        onClick: handleMenuClick,
      }}
      trigger={['click']}
      placement={placement}
      getPopupContainer={getPopupContainer}
      onOpenChange={onDropdownOpenChange}
      classNames={{ root: cx(styles.dropdownClass, dropdownClassname) }}
      popupRender={renderPopup}
      open={multipleMode ? isOpen : undefined}
      arrow={arrow}
    >
      {children}
    </AntDesignDropdown>
  );
};

export default Dropdown;
